import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, CheckCircle2, Star, Shield, Award, Users, Phone, MapPin } from 'lucide-react';
import { TREATMENTS, CLINIC_INFO } from '../data';
import { motion } from 'framer-motion';
import { useEffect } from 'react';

export default function Home() {
  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage";
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden pt-32 lg:pt-48">
        {/* Hero Image Overlay */}
        <div className="absolute inset-0 bg-blue-900/40 z-10" />
        <div className="absolute inset-0 z-0">
          <video
            src="/images/video/massage-1.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]">
              Sports- og Wellness Massage i Rødekro
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-10 leading-relaxed font-light">
              Fysiurgisk massage, wellness og gravid massage. Professionelle behandlinger skræddersyet til dine behov.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/bestil-tid"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all shadow-lg flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Bestil tid
              </Link>
              <Link
                to="/behandlinger"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all flex items-center justify-center"
              >
                Se behandlinger
                <ChevronRight className="w-5 h-5 ml-1" />
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1.5 h-1.5 bg-white rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Weekend Booking Banner */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 shadow-md relative z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-3">
              <span className="bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-white/30 animate-pulse">
                Nyhed
              </span>
              <p className="font-medium text-sm md:text-base">
                Nu kan du også bestille tid i weekenden! Jeg holder åbent både <strong className="font-bold">lørdag og søndag kl. 10.00 - 15.00</strong>.
              </p>
            </div>
            <Link
              to="/bestil-tid"
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-5 py-2 rounded-full text-sm font-bold transition-all shadow-sm shrink-0 flex items-center"
            >
              <Calendar className="w-4 h-4 mr-1.5" />
              Bestil weekendtid
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-50 rounded-full -z-10 animate-pulse" />
              <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-emerald-50 rounded-full -z-10 hidden lg:block" />
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <img
                  src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/f0a5b68d-00d1-4e4f-ab9e-07c5cc58f936.jpg"
                  alt="CDH Massage Klinik"
                  className="rounded-[2.5rem] shadow-2xl relative z-10 object-cover w-full h-[450px] md:h-[580px] lg:h-[700px] border-8 border-white"
                />
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.8 }}
                whileHover={{ y: -5 }}
                className="absolute -bottom-8 -right-8 bg-white p-8 rounded-[2rem] shadow-[0_20px_50px_rgba(8,112,184,0.12)] z-20 hidden md:block border border-blue-50/50 group cursor-default"
              >
                <div className="flex items-center space-x-5">
                  <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-4 rounded-2xl shadow-lg shadow-emerald-200 transition-transform group-hover:rotate-12">
                    <Star className="w-7 h-7 text-white fill-white" />
                  </div>
                  <div>
                    <div className="flex items-baseline space-x-1">
                      <p className="text-3xl font-black text-blue-900 leading-none">10+</p>
                      <span className="text-emerald-600 font-bold text-lg">år</span>
                    </div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-1.5">Erfaring</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="text-emerald-600 font-bold tracking-wider uppercase text-sm mb-4 block">Velkommen til klinikken</span>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6 leading-tight">
                Din krop i trygge og professionelle hænder
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed mb-8">
                <p>
                  Hos CDH Sports- og Wellness Massage har jeg siden 2012 haft fokus på at skabe de bedste rammer for dine behandlinger. Min klinik i Rødekro er et sted, hvor faglighed og personligt nærvær går hånd i hånd.
                </p>
                <p>
                  Har du f.eks. problemer med smerter eller spændinger i kroppen, så som spændingshovedpine, nakkesmerter, lændesmerter, tennisalbue eller lignende, så har du mulighed for at få en gang dybdegående fysiurgisk massage.
                </p>
              </div>

              {/* Online Booking Highlight */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 mb-8 flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-emerald-600 shrink-0" />
                <p className="text-xs sm:text-sm text-emerald-800 font-medium">
                  <strong>Nem online booking:</strong> Find nemt en ledig tid i mit bookingsystem, når det passer dig bedst. Det tager under 2 minutter!
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {[
                  "Fysiurgisk massage",
                  "Wellness massage",
                  "Gravid massage",
                  "Massage for 2 personer"
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="font-medium text-blue-900">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/om-mig"
                className="inline-flex items-center font-bold text-blue-700 hover:text-blue-900 transition-colors group"
              >
                Læs mere om Carsten
                <ChevronRight className="w-5 h-5 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments Teaser */}
      <section className="py-24 bg-blue-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Behandlinger</h2>
            <p className="text-gray-600 text-sm sm:text-base">
              Udforsk mit udvalg af professionelle massagebehandlinger, der er designet til at forbedre dit velvære og afhjælpe dine gener.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {TREATMENTS.filter(t => ['fysiurgisk-massage', 'gravid-massage', 'wellness-massage'].includes(t.id))
              .sort((a, b) => {
                const order = ['fysiurgisk-massage', 'gravid-massage', 'wellness-massage'];
                return order.indexOf(a.id) - order.indexOf(b.id);
              })
              .map((treatment) => (
              <motion.div
                whileHover={{ y: -10 }}
                key={treatment.id}
                className={`bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-blue-100 group ${
                  treatment.id === 'wellness-massage' ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div className="h-64 overflow-hidden relative">
                  <img
                    src={treatment.image}
                    alt={treatment.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-8 flex flex-col h-[calc(100%-16rem)]">
                  <h3 className="text-xl font-bold text-blue-900 mb-3 group-hover:text-blue-700 transition-colors">
                    {treatment.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-6 line-clamp-2 flex-grow">
                    {treatment.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                    <Link
                      to={`/behandlinger/${treatment.id}`}
                      className="text-[#3a38a4] font-bold text-xs sm:text-sm uppercase tracking-wider hover:text-blue-800 transition-colors"
                    >
                      Se detaljer
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/behandlinger"
              className="inline-flex items-center px-6 py-3.5 lg:px-8 lg:py-4 bg-white border-2 border-blue-100 text-blue-700 rounded-full font-bold hover:bg-blue-50 hover:border-blue-200 transition-all shadow-sm text-sm lg:text-base"
            >
              Se alle behandlinger
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Factors */}
      <section className="py-32 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Hvorfor vælge CDH Massage?</h2>
            <p className="text-gray-600 text-sm sm:text-base">Jeg kombinerer mange års erfaring med en personlig tilgang til dit velvære.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Shield className="w-7 h-7" />,
                title: "Tryg behandling",
                desc: "Altid i trygge og rolige rammer, hvor du er i centrum fra start til slut.",
                color: "blue"
              },
              {
                icon: <Award className="w-7 h-7" />,
                title: "Faglig erfaring",
                desc: "Mange års erfaring og dybdegående ekspertise inden for fysiurgisk massage.",
                color: "emerald"
              },
              {
                icon: <Star className="w-7 h-7" />,
                title: "Målbar effekt",
                desc: "Effektiv behandling målrettet dine specifikke spændinger og muskelsmerter.",
                color: "blue"
              },
              {
                icon: <Users className="w-7 h-7" />,
                title: "Velvære og ro",
                desc: "Giv dit sind en pause og din krop fornyet energi i en travl hverdag.",
                color: "emerald"
              }
            ].map((factor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white p-8 rounded-[2.5rem] border border-blue-50 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 group"
              >
                <div className={`w-16 h-16 ${factor.color === 'blue' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'} rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300`}>
                  {factor.icon}
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-4">{factor.title}</h3>
                <p className="text-gray-500 text-[13px] sm:text-sm leading-relaxed">{factor.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative rounded-[3rem] overflow-hidden group">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/111cf125-e389-46d1-8c9b-aaa41d94cdba.jpg" 
                alt="Wellness atmosphere" 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-blue-900/85 mix-blend-multiply" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 p-12 md:p-20 text-center">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
                  Klar til at bestille din næste behandling?
                </h2>
                <p className="text-blue-50 mb-12 text-base md:text-lg">
                  Book din tid online i dag. Det tager kun to minutter. Hvis du er i tvivl om valget af behandling, er du altid velkommen til at kontakte mig.
                </p>
                <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <Link
                    to="/bestil-tid"
                    className="bg-white text-blue-900 px-8 py-3.5 lg:px-10 lg:py-4 rounded-full font-bold text-base lg:text-lg hover:bg-emerald-400 hover:text-white transition-all shadow-xl hover:-translate-y-1"
                  >
                    Bestil tid online
                  </Link>
                  <a
                    href={`tel:${CLINIC_INFO.phone}`}
                    className="backdrop-blur-md border-2 border-white/30 text-white px-8 py-3.5 lg:px-10 lg:py-4 rounded-full font-bold text-base lg:text-lg hover:bg-white/10 transition-all flex items-center justify-center hover:-translate-y-1"
                  >
                    Ring: {CLINIC_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info bar */}
      <div className="bg-emerald-600 py-6">
        <div className="container mx-auto px-4 text-center text-white font-medium">
          <p className="text-sm sm:text-base flex flex-wrap justify-center items-center gap-x-8 gap-y-2">
            <span className="flex items-center">
              <MapPin className="w-5 h-5 mr-2 opacity-70" /> 
              <a 
                href="https://maps.app.goo.gl/zduoinBMcwsKXgZPA"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {CLINIC_INFO.address}
              </a>
            </span>
            <span className="flex items-center"><Phone className="w-5 h-5 mr-2 opacity-70" /> <a href={`tel:${CLINIC_INFO.phone}`} className="hover:underline">{CLINIC_INFO.phone}</a></span>
          </p>
        </div>
      </div>
    </div>
  );
}
